import {
  UserOrderInfo,
  V3_POOL_SPLITTER,
  pointToPrice,
  cancel_order,
} from '../../../services/swapV3';
import { TokenMetadata } from '../../../services/ft-contract';
import React, { useState, useContext, useMemo, Fragment, useRef } from 'react';
import { getTxId } from '../../../services/indexer';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  toReadableNumber,
  scientificNotationToString,
  checkAllocations,
  calculateFeePercent,
  ONLY_ZEROS,
  toPrecision,
} from '../../../utils/numbers';
import Big from 'big.js';
import { toRealSymbol } from '../../../utils/token';
import BigNumber from 'bignumber.js';
import QuestionMark from '../../farm/QuestionMark';
import CustomTooltip from 'src/components/customTooltip/customTooltip';
import { ButtonTextWrapper } from '../../button/Button';
import moment from 'moment';
import { TIMESTAMP_DIVISOR } from 'src/constants/timestampDivisor';
import { PriceContext, MobileInfoBanner } from './myOrderShared';
import { isMobile, isClientMobile } from '../../../utils/device';
import { ExclamationTip } from '../../layout/TipWrapper';
import { MyOrderInstantSwapArrowRight, FilledEllipse } from '../../icon/swapV3';
import { BsCheckCircle, HiOutlineExternalLink } from '../../reactIcons/index';
import getConfig from '../../../services/config';
import {
  NearblocksIcon,
  PikespeakIcon,
  TxLeftArrow,
} from 'src/components/icon/Pool';
import { MyOrderMobileArrow } from '../../icon/Arrows';
import { TOKEN_LIST_FOR_RATE } from '../../../services/dcl/dclDisplay';

export function ActiveLine({
  tokensMap,
  order,
  index,
  sellAmountToBuyAmount,
  orderTx,
  hoverOn,
  setHoverOn,
}: {
  order: UserOrderInfo;
  index: number;
  sellAmountToBuyAmount: any;
  tokensMap: { [key: string]: TokenMetadata };
  orderTx: string;
  hoverOn: number;
  setHoverOn: React.Dispatch<React.SetStateAction<number>>;
}) {
  const [loadingStates, setLoadingStates] = useState({});
  const [hoveredTx, setHoveredTx] = useState(null);
  const closeTimeoutRef = useRef(null);

  const handleMouseEnter = (receipt_id) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setHoveredTx(receipt_id);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredTx(null);
    }, 200);
  };

  async function handleTxClick(receipt_id, url) {
    setLoadingStates((prevStates) => ({ ...prevStates, [receipt_id]: true }));
    try {
      const txIdResult = await getTxId(receipt_id);
      if (txIdResult && txIdResult.receipts && txIdResult.receipts.length > 0) {
        const txHash = txIdResult.receipts[0].originated_from_transaction_hash;
        window.open(`${url}/${txHash}`, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error(
        'An error occurred while fetching transaction data:',
        error
      );
    } finally {
      setLoadingStates((prevStates) => ({
        ...prevStates,
        [receipt_id]: false,
      }));
    }
  }
  const [claimLoading, setClaimLoading] = useState<boolean>(false);
  const intl = useIntl();

  const [cancelLoading, setCancelLoading] = useState<boolean>(false);

  const buyToken = tokensMap[order.buy_token];

  const sellToken = tokensMap[order.sell_token];

  if (!buyToken || !sellToken) return null;

  const swapIn = toReadableNumber(
    sellToken.decimals,
    scientificNotationToString(
      new Big(order.original_deposit_amount || '0')
        .minus(order.original_amount || '0')
        .toString()
    )
  );

  const swapOut = toReadableNumber(
    buyToken.decimals,
    order.swap_earn_amount || '0'
  );

  const orderIn = toReadableNumber(
    sellToken.decimals,
    order.original_amount || '0'
  );

  const totalIn = toReadableNumber(
    sellToken.decimals,
    order.original_deposit_amount || '0'
  );

  const calPoint =
    sellToken.id === order.pool_id.split(V3_POOL_SPLITTER)[0]
      ? order.point
      : -order.point;

  const price = pointToPrice({
    tokenA: sellToken,
    tokenB: buyToken,
    point: calPoint,
  });

  const unClaimedAmount = toReadableNumber(
    buyToken.decimals,
    order.unclaimed_amount || '0'
  );

  const claimedAmount = toReadableNumber(
    buyToken.decimals,
    scientificNotationToString(
      new Big(order.bought_amount || '0')
        .minus(order.unclaimed_amount || '0')
        .toString()
    )
  );

  const buyAmountToSellAmount = (
    undecimaled_amount: string,
    order: UserOrderInfo,
    price: string
  ) => {
    const sell_amount = new Big(
      toReadableNumber(
        tokensMap[order.buy_token].decimals,
        undecimaled_amount || '0'
      )
    )
      .div(price)
      .toString();
    return scientificNotationToString(sell_amount);
  };

  const unClaimedAmountIn = buyAmountToSellAmount(
    order.unclaimed_amount || '0',
    order,
    price
  );

  const claimedAmountIn = buyAmountToSellAmount(
    scientificNotationToString(
      new Big(order.bought_amount || '0')
        .minus(order.unclaimed_amount || '0')
        .toString()
    ),
    order,
    price
  );

  const buyAmountRaw = sellAmountToBuyAmount(
    order.original_amount,
    order,
    price
  );

  const buyAmount = new Big(buyAmountRaw).gt(
    toReadableNumber(buyToken.decimals, order.bought_amount || '0')
  )
    ? buyAmountRaw
    : toReadableNumber(buyToken.decimals, order.bought_amount || '0');

  const totalOut = scientificNotationToString(
    new Big(buyAmount).plus(swapOut).toString()
  );

  const pendingAmount = scientificNotationToString(
    new Big(toPrecision(buyAmount || '0', 5, false, false) || 0)
      .minus(
        toPrecision(
          toReadableNumber(buyToken.decimals, order.bought_amount || '0') ||
            '0',
          5,
          false,
          false
        )
      )
      .toString()
  );

  const pUnClaimedAmount = new Big(unClaimedAmount)
    .div(buyAmount)
    .times(100)
    .toNumber();

  const pClaimedAmount = new Big(claimedAmount)
    .div(buyAmount)
    .times(100)
    .toNumber();

  const pPendingAmount = new Big(pendingAmount)
    .div(buyAmount)
    .times(100)
    .toNumber();

  const displayPercents = checkAllocations('100', [
    pClaimedAmount > 0 && pClaimedAmount < 5
      ? '5'
      : scientificNotationToString(pClaimedAmount.toString()),
    pUnClaimedAmount > 0 && pUnClaimedAmount < 5
      ? '5'
      : scientificNotationToString(pUnClaimedAmount.toString()),

    pPendingAmount > 0 && pPendingAmount < 5
      ? '5'
      : scientificNotationToString(pPendingAmount.toString()),
  ]);

  const getUnclaimAmountTip = () => {
    return `
      <div 
        class="flex flex-col relative text-xs min-w-36 text-farmText z-50"
      >
      ${
        ONLY_ZEROS.test(claimedAmount)
          ? ''
          : `
      <div class="flex items-center justify-between my-1">
          <span class="flex items-center mr-1">
              <div class="w-1.5 h-1.5 rounded-full bg-gradientFrom mr-1">
              </div>

              ${intl.formatMessage({
                id: 'claimed_upper',
                defaultMessage: 'Claimed',
              })}

          </span>

          <span>
          ${
            Number(claimedAmount) > 0 && Number(claimedAmount) < 0.001
              ? '< 0.001'
              : toPrecision(claimedAmount, 3)
          }
          </span>

      </div>
      `
      }


      ${
        ONLY_ZEROS.test(unClaimedAmount)
          ? ''
          : `<div class="flex items-center my-1 justify-between">
          <span class="flex items-center mr-1">
              <div class="w-1.5 h-1.5 rounded-full bg-deepBlue mr-1">
              </div>

              ${intl.formatMessage({
                id: 'filled',
                defaultMessage: 'Filled',
              })}

          </span>

          <span>
          ${
            Number(unClaimedAmount) > 0 && Number(unClaimedAmount) < 0.001
              ? '< 0.001'
              : toPrecision(unClaimedAmount, 3)
          }
          </span>

      </div>`
      }

      ${
        ONLY_ZEROS.test(pendingAmount)
          ? ''
          : `<div class="flex items-center my-1 justify-between">
          <span class="flex items-center ">
              <div class="w-1.5 h-1.5 rounded-full bg-primaryText mr-1">
              </div>

              ${intl.formatMessage({
                id: 'open_my_order',
                defaultMessage: 'Open',
              })}

          </span>

          <span>
          ${
            Number(pendingAmount) > 0 && Number(pendingAmount) < 0.001
              ? '< 0.001'
              : toPrecision(pendingAmount, 3)
          }

          </span>

      </div>`
      }

      </div>
  `;
  };

  const sellTokenAmount = (
    <span className="flex py-4 pl-3  flex-shrink-0 items-center">
      <img
        src={sellToken.icon}
        className="border border-gradientFrom rounded-full w-7 h-7"
        alt=""
      />

      <div className="flex   xs:flex-row flex-col ml-2">
        <span className="text-white text-sm mr-2" title={orderIn}>
          {Number(orderIn) > 0 && Number(orderIn) < 0.01
            ? '< 0.01'
            : toPrecision(orderIn, 2)}
        </span>

        <span className="text-v3SwapGray text-xs xs:relative xs:top-0.5">
          {toRealSymbol(sellToken.symbol)}
        </span>
      </div>
    </span>
  );

  const buyTokenAmount = (
    <span className="flex items-center ">
      <img
        src={buyToken.icon}
        className="border flex-shrink-0 border-gradientFrom rounded-full w-7 h-7"
        alt=""
      />

      <div className="flex xs:flex-row flex-col ml-2">
        <span
          className="text-white mr-2 text-sm whitespace-nowrap"
          title={buyAmount}
        >
          {Number(buyAmount) > 0 && Number(buyAmount) < 0.01
            ? '< 0.01'
            : toPrecision(buyAmount, 2)}
        </span>

        <span className="text-v3SwapGray text-xs xs:relative xs:top-0.5">
          {toRealSymbol(buyToken.symbol)}
        </span>
      </div>
    </span>
  );

  const fee = Number(order.pool_id.split(V3_POOL_SPLITTER)[2]);

  const feeTier = (
    <span className="rounded-lg relative xsm:right-0 xsm:bg-none right-3 text-left  text-primaryText p-1 lg:bg-menuMoreBgColor xs:text-white">
      {`${toPrecision(calculateFeePercent(fee / 100).toString(), 2)}% `}
    </span>
  );

  const sort =
    TOKEN_LIST_FOR_RATE.indexOf(sellToken?.symbol) > -1 && +price !== 0;
  const orderRate = useMemo(() => {
    let displayPrice = price;
    if (sort) {
      displayPrice = new BigNumber(1).dividedBy(price).toFixed();
    }
    return (
      <span className="whitespace-nowrap  col-span-1 flex items-start xs:flex-row xs:items-center flex-col relative  xs:right-0">
        <span className="mr-1 text-white text-sm" title={displayPrice}>
          {toPrecision(displayPrice, 2)}
        </span>
        <span className="text-v3SwapGray text-xs xs:hidden">
          {`${toRealSymbol(
            sort ? sellToken?.symbol : buyToken.symbol
          )}/${toRealSymbol(sort ? buyToken.symbol : sellToken.symbol)}`}
        </span>
        <span className="text-white text-sm lg:hidden md:hidden">
          {`${toRealSymbol(sort ? sellToken.symbol : buyToken.symbol)}`}
        </span>
      </span>
    );
  }, [buyToken, sellToken, price]);

  const unclaimTip = (
    <div
      className="text-xs xs:relative xs:bottom-2 mt-1 mr-1 w-20 xs:w-full flex items-center xs:flex-row-reverse"
      data-type="info"
      data-place="bottom"
      data-multiline={true}
      data-class="reactTip"
      data-tooltip-html={getUnclaimAmountTip()}
      data-tooltip-id={'unclaim_tip_' + order.order_id}
    >
      <span className="mr-1 xs:ml-2">
        <QuestionMark color="dark" />
      </span>
      <div className="flex items-center w-full">
        {displayPercents.map((percent, percentIndex) => {
          if (ONLY_ZEROS.test(percent)) return null;

          const bgColor =
            percentIndex === 0
              ? 'bg-gradientFrom'
              : percentIndex === 1
              ? 'bg-deepBlue'
              : 'bg-primaryText';

          return (
            <div
              className={`mx-px h-1 xs:h-2 rounded-lg ${bgColor}`}
              style={{
                width: percent + '%',
              }}
            />
          );
        })}
      </div>
      <CustomTooltip
        className="w-20"
        id={'unclaim_tip_' + order.order_id}
        place="bottom"
      />
    </div>
  );

  const claimButton = (
    <button
      className={`rounded-lg text-xs xs:text-sm xs:w-full ml-1.5 mr-2 py-2 px-9 ${
        ONLY_ZEROS.test(unClaimedAmount)
          ? 'text-v3SwapGray cursor-not-allowe bg-black opacity-20 cursor-not-allowed'
          : `text-white bg-deepBlue hover:text-white hover:bg-deepBlueHover ${
              claimLoading ? ' text-white bg-deepBlueHover ' : ''
            }`
      }`}
      type="button"
      disabled={ONLY_ZEROS.test(unClaimedAmount)}
      // disabled
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();

        setClaimLoading(true);

        cancel_order({
          order_id: order.order_id,
          undecimal_amount: '0',
        });
      }}
      style={{
        height: '34px',
      }}
    >
      <ButtonTextWrapper
        Text={() => <FormattedMessage id="claim" defaultMessage={'Claim'} />}
        loading={claimLoading}
      ></ButtonTextWrapper>
    </button>
  );

  const unclaim = (
    <span className="whitespace-nowrap col-span-2 flex xs:flex-col items-center ">
      <div>
        <div className="flex items-center xs:justify-end">
          <img
            src={buyToken.icon}
            className="border border-gradientFrom rounded-full w-4 h-4"
            alt=""
          />
          <span
            className="text-white text-sm mx-1"
            title={toReadableNumber(
              buyToken.decimals,
              order.unclaimed_amount || '0'
            )}
          >
            {Number(
              toReadableNumber(buyToken.decimals, order.unclaimed_amount || '0')
            ) > 0 &&
            Number(
              toReadableNumber(buyToken.decimals, order.unclaimed_amount || '0')
            ) < 0.001
              ? '< 0.001'
              : toPrecision(
                  toReadableNumber(
                    buyToken.decimals,
                    order.unclaimed_amount || '0'
                  ),
                  3
                )}
          </span>
        </div>
        <div className="xs:hidden">{unclaimTip}</div>
      </div>
    </span>
  );

  const created = (
    <span className=" relative  whitespace-nowrap    text-primaryText xs:text-xs flex flex-col   xsm:justify-center  text-left xs:opacity-50">
      <span className="xsm:hidden">
        {moment(
          Math.floor(Number(order.created_at) / TIMESTAMP_DIVISOR) * 1000
        ).format('YYYY-MM-DD')}
      </span>
      <span className="xsm:hidden">
        {moment(
          Math.floor(Number(order.created_at) / TIMESTAMP_DIVISOR) * 1000
        ).format('HH:mm')}
      </span>

      <span className="lg:hidden text-center relative bottom-2">
        {moment(
          Math.floor(Number(order.created_at) / TIMESTAMP_DIVISOR) * 1000
        ).format('YYYY-MM-DD HH:mm')}
      </span>
    </span>
  );

  const actions = (
    <button
      className={`border col-span-1 rounded-lg xs:text-sm xs:w-full text-xs justify-self-end py-2 px-9 ${
        cancelLoading ? 'border border-transparent text-black bg-warn ' : ''
      }  border-warn border-opacity-20 text-warn  ${'hover:border hover:border-transparent hover:text-black hover:bg-warn'}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setCancelLoading(true);
        cancel_order({
          order_id: order.order_id,
        });
      }}
      // disabled={ONLY_ZEROS.test(order.remain_amount)}
      // disabled
    >
      <ButtonTextWrapper
        Text={() => <FormattedMessage id="cancel" defaultMessage={'Cancel'} />}
        loading={cancelLoading}
      />
    </button>
  );

  const tokenPrice = useContext(PriceContext);

  const sellTokenPrice = tokenPrice?.[sellToken.id]?.price || null;
  const buyTokenPrice = tokenPrice?.[buyToken.id]?.price || null;
  function instant_swap_tip() {
    const token_sell_symbol = toRealSymbol(sellToken.symbol);
    const token_buy_symbol = toRealSymbol(buyToken.symbol);
    const sell_token_price = sellTokenPrice
      ? `($${toPrecision(sellTokenPrice, 2)})`
      : '';
    const buy_token_price = buyTokenPrice
      ? `($${toPrecision(buyTokenPrice, 2)})`
      : '';
    let rate = new Big(swapOut).div(ONLY_ZEROS.test(swapIn) ? 1 : swapIn);
    if (sort) {
      rate = new Big(1).div(rate.eq(0) ? '1' : rate);
    }
    const display_rate = rate.toFixed(3);
    let result = '';
    if (sort) {
      result = `1 ${token_buy_symbol} ${buy_token_price} = ${display_rate} ${token_sell_symbol}`;
    } else {
      result = `1 ${token_sell_symbol} ${sell_token_price} = ${display_rate} ${token_buy_symbol}`;
    }
    return result;
  }
  const swapBanner = (
    <td
      colSpan={8}
      className="xsm:block xsm:rounded-xl xs:text-xs text-v3SwapGray w-full relative lg:bottom-1.5 lg:pt-6 xsm:pt-4 bg-portfolioBgColor"
    >
      {new Big(order.original_deposit_amount || '0')
        .minus(order.original_amount || '0')
        .gt(0) && (
        <>
          <div className="flex items-center px-4 pb-4  justify-between ">
            <span className="flex items-center">
              <FormattedMessage
                id="initial_order"
                defaultMessage={'Initial Order'}
              />
              {!isMobile() && (
                <ExclamationTip
                  id="this_order_has_been_partially_filled"
                  defaultMessage="This order has been partially filled "
                  dataPlace="bottom"
                  colorhex="#7E8A93"
                  uniquenessId={
                    'this_order_has_been_partially_filled' + order.order_id
                  }
                />
              )}
            </span>

            <span className="flex items-center text-xs">
              <span title={totalIn} className="text-v3SwapGray">
                {Number(totalIn) > 0 && Number(totalIn) < 0.01
                  ? '< 0.01'
                  : toPrecision(totalIn, 2)}
              </span>

              <span className="ml-1.5">{toRealSymbol(sellToken.symbol)}</span>
              <span className="mx-6 xs:mx-2 text-white xs:text-v3SwapGray">
                {isClientMobile() ? (
                  <MyOrderInstantSwapArrowRight />
                ) : (
                  <MyOrderInstantSwapArrowRight />
                )}
              </span>
              <span
                title={toPrecision(totalOut, buyToken.decimals)}
                className="text-v3SwapGray"
              >
                {Number(totalOut) > 0 && Number(totalOut) < 0.01
                  ? '< 0.01'
                  : toPrecision(totalOut, 2)}
              </span>

              <span className="ml-1.5">{toRealSymbol(buyToken.symbol)}</span>
            </span>
          </div>

          <div className="frcb px-4 pb-4">
            <span className="flex items-center ">
              <FormattedMessage
                id={isMobile() ? 'filled_via_swap' : 'instants_swap'}
                defaultMessage={isMobile() ? 'Filled via swap' : 'Instant Swap'}
              />
              {!isMobile() && (
                <ExclamationTip
                  colorhex="#7E8A93"
                  id={instant_swap_tip()}
                  defaultMessage={instant_swap_tip()}
                  dataPlace="bottom"
                  uniquenessId={'instant_swap_tip' + order.order_id}
                />
              )}
            </span>

            <span className="frcb xsm:justify-start lg:min-w-p300">
              <div className="frcs text-xs  pr-2 text-v3SwapGray">
                <BsCheckCircle
                  className="mr-1.5"
                  fill="#42bb17"
                  stroke="#42BB17"
                />
                <span className="xsm:hidden">
                  <FormattedMessage
                    id="swappped"
                    defaultMessage={'Swapped'}
                  ></FormattedMessage>
                </span>
              </div>

              <div className="flex items-center justify-end">
                <span title={swapIn} className="text-v3SwapGray">
                  {Number(swapIn) > 0 && Number(swapIn) < 0.01
                    ? '< 0.01'
                    : toPrecision(swapIn, 2)}
                </span>

                <span className="ml-1.5">{toRealSymbol(sellToken.symbol)}</span>
                <span className="mx-6 xs:mx-2 text-v3SwapGray">
                  {isClientMobile() ? (
                    <MyOrderInstantSwapArrowRight />
                  ) : (
                    <MyOrderInstantSwapArrowRight />
                  )}
                </span>
                <span title={swapOut} className="text-v3SwapGray">
                  {Number(swapOut) > 0 && Number(swapOut) < 0.01
                    ? '< 0.01'
                    : toPrecision(swapOut, 2)}
                </span>

                <span className="ml-1.5">{toRealSymbol(buyToken.symbol)}</span>
              </div>
            </span>
          </div>
        </>
      )}

      <div className="flex items-start xsm:hidden justify-between px-4 pb-4">
        <span className="xsm:text-v3SwapGray">
          <FormattedMessage
            id="executing_capital"
            defaultMessage={'Executing'}
          />
        </span>

        <div className="flex flex-col items-end">
          <span className="frcb min-w-p300">
            <div className="frcs text-xs pr-2 text-v3SwapGray">
              <BsCheckCircle
                className="mr-1.5"
                fill="#00D6AF"
                stroke="#00D6AF"
              />

              <FormattedMessage
                id="claimed"
                defaultMessage={'Claimed'}
              ></FormattedMessage>
            </div>

            <div className="flex items-center justify-end">
              <span
                title={toPrecision(claimedAmountIn, sellToken.decimals)}
                className="text-v3SwapGray"
              >
                {Number(claimedAmountIn) > 0 && Number(claimedAmountIn) < 0.01
                  ? '< 0.01'
                  : toPrecision(claimedAmountIn, 2)}
              </span>

              <span className="ml-1.5 xsm:text-v3SwapGray">
                {toRealSymbol(sellToken.symbol)}
              </span>
              <span className="mx-6 xs:mx-2 text-v3SwapGray">
                {isClientMobile() ? (
                  <MyOrderInstantSwapArrowRight />
                ) : (
                  <MyOrderInstantSwapArrowRight />
                )}
              </span>
              <span title={claimedAmount} className="text-v3SwapGray">
                {Number(claimedAmount) > 0 && Number(claimedAmount) < 0.01
                  ? '< 0.01'
                  : toPrecision(claimedAmount, 2)}
              </span>

              <span className="ml-1.5 xsm:text-v3SwapGray">
                {toRealSymbol(buyToken.symbol)}
              </span>
            </div>
          </span>

          <span className=" pt-4  frcb min-w-p300">
            <div className="frcs text-xs pr-2 text-v3SwapGray">
              <span className="mr-1.5">
                <FilledEllipse></FilledEllipse>
              </span>

              <FormattedMessage
                id="filled"
                defaultMessage={'Filled'}
              ></FormattedMessage>
            </div>

            <div className="flex items-center justify-end">
              <span
                title={toPrecision(unClaimedAmountIn, sellToken.decimals)}
                className="text-white font-gothamBold"
              >
                {Number(unClaimedAmountIn) > 0 &&
                Number(unClaimedAmountIn) < 0.01
                  ? '< 0.01'
                  : toPrecision(unClaimedAmountIn, 2)}
              </span>

              <span className="ml-1.5 xsm:text-v3SwapGray">
                {toRealSymbol(sellToken.symbol)}
              </span>
              <span className="mx-6 xs:mx-2 text-v3SwapGray">
                {isClientMobile() ? (
                  <MyOrderInstantSwapArrowRight />
                ) : (
                  <MyOrderInstantSwapArrowRight />
                )}
              </span>
              <span
                title={unClaimedAmount}
                className="text-white font-gothamBold"
              >
                {Number(unClaimedAmount) > 0 && Number(unClaimedAmount) < 0.01
                  ? '< 0.01'
                  : toPrecision(unClaimedAmount, 2)}
              </span>

              <span className="ml-1.5 xsm:text-v3SwapGray">
                {toRealSymbol(buyToken.symbol)}
              </span>
            </div>
          </span>
        </div>
      </div>

      <div className="lg:hidden flex items-center justify-end pr-4 pb-2">
        <div className="flex  max-w-max text-primaryText bg-black bg-opacity-20 rounded-md px-2 py-1 items-center justify-end lg:hidden">
          <span className="">1</span>

          <span className="ml-1.5">
            {toRealSymbol(sort ? buyToken.symbol : sellToken.symbol)}
          </span>

          {tokenPrice?.[sort ? buyToken?.id : sellToken?.id]?.price && (
            <span className="ml-1">{`($${
              tokenPrice?.[sort ? buyToken?.id : sellToken?.id].price
            })`}</span>
          )}

          <span className="mx-6 xs:mx-2 ">=</span>
          <span className="">
            {toPrecision(
              scientificNotationToString(
                new Big(sort ? swapIn || 0 : swapOut || 0)
                  .div(
                    Number(sort ? swapOut : swapIn) === 0
                      ? 1
                      : sort
                      ? swapOut
                      : swapIn
                  )
                  .toString()
              ),
              3
            )}
          </span>

          <span className="ml-1.5">
            {toRealSymbol(sort ? sellToken.symbol : buyToken.symbol)}
          </span>
        </div>
      </div>
    </td>
  );
  return (
    <Fragment>
      <tr>
        <td colSpan={9}>
          <div className="pb-2.5"></div>
        </td>
      </tr>
      <tr
        className={`mb-4 overflow-visible   xs:hidden px-4 py-3 text-sm   z-20   relative  w-full  items-center   ${
          hoverOn === index
            ? 'bg-portfolioBarBgColor rounded-t-xl'
            : 'bg-portfolioBgColor rounded-xl'
        }`}
        onMouseEnter={() => {
          setHoverOn(index);
        }}
        style={{
          zIndex: 21,
        }}
      >
        <td className={hoverOn === index ? ' rounded-tl-xl' : ' rounded-l-xl'}>
          {sellTokenAmount}
        </td>

        <td>
          <span className="text-white text-lg frcs w-7 xs:hidden ">
            <MyOrderInstantSwapArrowRight />
          </span>
        </td>

        <td>{buyTokenAmount}</td>

        <td>{feeTier}</td>

        <td>
          <div className="w-14"></div>
        </td>

        <td>{orderRate}</td>

        <td>{created}</td>

        <td className={hoverOn === index ? ' rounded-tr-xl' : ' rounded-r-xl'}>
          {unclaim}
        </td>

        {/* {actions} */}
      </tr>

      {hoverOn === index && (
        <>
          <tr className="xs:flex z-20 relative  xs:flex-col whitespace-nowrap xs:bg-cardBg xs:bg-opacity-50  bottom-2 xs:bottom-0 w-full text-sm text-v3SwapGray bg-cardBg rounded-xl px-5 pb-5 pt-10 xs:px-3 xs:py-4 xs:text-xs">
            {swapBanner}
          </tr>

          <tr className="relative bottom-6 rounded-b-xl bg-portfolioBarBgColor">
            <td colSpan={8} className="rounded-b-xl">
              <div className="frcb pb-3 py-6 px-4 text-xs">
                <div className="frcs relative">
                  {!!orderTx && (
                    <a
                      className="flex items-center text-v3SwapGray cursor-pointer"
                      onMouseEnter={() => handleMouseEnter(orderTx)}
                      onMouseLeave={handleMouseLeave}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                    >
                      {loadingStates[orderTx] ? (
                        <>
                          Tx
                          <span className="loading-dots"></span>
                        </>
                      ) : (
                        <>
                          Tx
                          <span className="ml-1.5">
                            <HiOutlineExternalLink />
                          </span>
                        </>
                      )}
                      {hoveredTx === orderTx && (
                        <div className="w-44 absolute top-6 left-0 bg-poolDetaileTxBgColor border border-poolDetaileTxBorderColor rounded-lg p-2 shadow-lg z-50">
                          <div className="flex flex-col">
                            <div
                              className="mb-2 px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                              onMouseEnter={(event) => {
                                const arrow = event.currentTarget.querySelector(
                                  '.arrow'
                                ) as HTMLElement;
                                if (arrow) {
                                  arrow.style.display = 'block';
                                }
                              }}
                              onMouseLeave={(event) => {
                                const arrow = event.currentTarget.querySelector(
                                  '.arrow'
                                ) as HTMLElement;
                                if (arrow) {
                                  arrow.style.display = 'none';
                                }
                              }}
                              onClick={() =>
                                handleTxClick(
                                  orderTx,
                                  `${getConfig().explorerUrl}/txns`
                                )
                              }
                            >
                              <NearblocksIcon />
                              <p className="ml-2 text-sm">nearblocks</p>
                              <div
                                className="ml-3 arrow"
                                style={{ display: 'none' }}
                              >
                                <TxLeftArrow />
                              </div>
                            </div>
                            <div
                              className="px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                              onMouseEnter={(event) => {
                                const arrow = event.currentTarget.querySelector(
                                  '.arrow'
                                ) as HTMLElement;
                                if (arrow) {
                                  arrow.style.display = 'block';
                                }
                              }}
                              onMouseLeave={(event) => {
                                const arrow = event.currentTarget.querySelector(
                                  '.arrow'
                                ) as HTMLElement;
                                if (arrow) {
                                  arrow.style.display = 'none';
                                }
                              }}
                              onClick={() =>
                                handleTxClick(
                                  orderTx,
                                  `${
                                    getConfig().pikespeakUrl
                                  }/transaction-viewer`
                                )
                              }
                            >
                              <PikespeakIcon />
                              <p className="ml-2 text-sm">Pikespeak...</p>
                              <div
                                className="ml-3 arrow"
                                style={{ display: 'none' }}
                              >
                                <TxLeftArrow />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </a>
                  )}
                </div>

                <div className="frcs">
                  {claimButton}

                  {actions}
                </div>
              </div>
            </td>
          </tr>
        </>
      )}

      {/* {hover && !ONLY_ZEROS.test(swapIn || '0') ? swapBanner : null} */}

      <div
        className="w-full mb-4 md:hidden lg:hidden"
        style={{
          zIndex: 20 - index,
        }}
      >
        {/* title */}
        <div className="rounded-t-xl relative bg-orderMobileTop px-3 pt-3">
          <div className="flex items-center relative justify-between">
            {sellTokenAmount}
            <MyOrderMobileArrow />
            {buyTokenAmount}
          </div>

          {created}

          <div className="absolute right-4 bottom-2.5 z-50  text-xs">
            {!!orderTx && (
              <a
                className="flex items-center text-v3SwapGray cursor-pointer"
                onMouseEnter={() => handleMouseEnter(orderTx)}
                onMouseLeave={handleMouseLeave}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {loadingStates[orderTx] ? (
                  <>
                    Tx
                    <span className="loading-dots"></span>
                  </>
                ) : (
                  <>
                    Tx
                    <span className="ml-1.5">
                      <HiOutlineExternalLink />
                    </span>
                  </>
                )}
                {hoveredTx === orderTx && (
                  <div className="w-44 absolute top-5 right-0 bg-poolDetaileTxBgColor border border-poolDetaileTxBorderColor rounded-lg p-2 shadow-lg z-50">
                    <div className="flex flex-col">
                      <div
                        className="mb-2 px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                        onMouseEnter={(event) => {
                          const arrow = event.currentTarget.querySelector(
                            '.arrow'
                          ) as HTMLElement;
                          if (arrow) {
                            arrow.style.display = 'block';
                          }
                        }}
                        onMouseLeave={(event) => {
                          const arrow = event.currentTarget.querySelector(
                            '.arrow'
                          ) as HTMLElement;
                          if (arrow) {
                            arrow.style.display = 'none';
                          }
                        }}
                        onClick={() =>
                          handleTxClick(
                            orderTx,
                            `${getConfig().explorerUrl}/txns`
                          )
                        }
                      >
                        <NearblocksIcon />
                        <p className="ml-2 text-sm">nearblocks</p>
                        <div className="ml-3 arrow" style={{ display: 'none' }}>
                          <TxLeftArrow />
                        </div>
                      </div>
                      <div
                        className="px-3 py-2 hover:bg-poolDetaileTxHoverColor text-white rounded-md flex items-center"
                        onMouseEnter={(event) => {
                          const arrow = event.currentTarget.querySelector(
                            '.arrow'
                          ) as HTMLElement;
                          if (arrow) {
                            arrow.style.display = 'block';
                          }
                        }}
                        onMouseLeave={(event) => {
                          const arrow = event.currentTarget.querySelector(
                            '.arrow'
                          ) as HTMLElement;
                          if (arrow) {
                            arrow.style.display = 'none';
                          }
                        }}
                        onClick={() =>
                          handleTxClick(
                            orderTx,
                            `${getConfig().pikespeakUrl}/transaction-viewer`
                          )
                        }
                      >
                        <PikespeakIcon />
                        <p className="ml-2 text-sm">Pikespeak...</p>
                        <div className="ml-3 arrow" style={{ display: 'none' }}>
                          <TxLeftArrow />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </a>
            )}
          </div>
        </div>
        {/*  content */}
        <div className="rounded-b-xl p-3 bg-cardBg">
          <MobileInfoBanner
            text={
              <FormattedMessage id="fee_tiers" defaultMessage={'Fee Tiers'} />
            }
            value={feeTier}
          />

          <MobileInfoBanner
            text={`1 ${toRealSymbol(
              sort ? buyToken.symbol : tokensMap[order.sell_token].symbol
            )} Price`}
            value={orderRate}
          />

          <MobileInfoBanner
            text={
              <FormattedMessage defaultMessage={'Executed'} id="executed" />
            }
            value={unclaim}
          />

          {unclaimTip}

          <div className="flex items-center w-full xs:mt-2">
            {actions}
            {claimButton}
          </div>
        </div>

        {/* swap banner */}
        {new Big(order.original_deposit_amount || '0')
          .minus(order.original_amount || '0')
          .gt(0)
          ? swapBanner
          : null}
      </div>
    </Fragment>
  );
}
